import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShoppingCart, ArrowLeft, Plus, Minus, Package } from 'lucide-react';
import { Button } from '@caruso/ui';
import { storeService } from '@/services/store.service';
import { useCart } from '@/contexts/cart-context';
import { StoreLayout } from '@/components/layouts/store-layout';

export function StoreProductPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addItem, totalItems } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);

  const { data: product, isLoading } = useQuery({
    queryKey: ['store-product', id],
    queryFn: () => storeService.getProduct(id!),
    enabled: !!id,
  });

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(price);
  };

  const handleAddToCart = () => {
    if (!product) return;

    addItem(
      {
        id: product.id,
        name: product.name,
        price: product.price,
        image: product.images.find((img) => img.isPrimary)?.url || product.images[0]?.url,
        stock: product.stock,
      },
      quantity
    );

    navigate('/loja/carrinho');
  };

  const incrementQuantity = () => {
    if (product?.stock && quantity >= product.stock) return;
    setQuantity((q) => q + 1);
  };

  const decrementQuantity = () => {
    if (quantity > 1) setQuantity((q) => q - 1);
  };

  if (isLoading) {
    return (
      <StoreLayout>
        <div className="flex items-center justify-center py-12">
          <div className="text-brand-ivory/50">Carregando produto...</div>
        </div>
      </StoreLayout>
    );
  }

  if (!product) {
    return (
      <StoreLayout>
        <div className="text-center py-12">
          <Package className="w-16 h-16 text-brand-gold/40 mx-auto mb-4" />
          <p className="text-brand-ivory/50 mb-4">Produto não encontrado</p>
          <Button variant="outline" onClick={() => navigate('/loja')}>
            Voltar para a loja
          </Button>
        </div>
      </StoreLayout>
    );
  }

  const isOutOfStock = product.stock !== undefined && product.stock !== null && product.stock <= 0;

  return (
    <StoreLayout>
      <div className="space-y-6">
        {/* Back button */}
        <Button variant="ghost" onClick={() => navigate('/loja')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar para a loja
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Image Gallery */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="space-y-4">
              {/* Main Image */}
              <div className="aspect-square bg-brand-ink rounded-lg overflow-hidden">
                {product.images.length > 0 ? (
                  <img
                    src={product.images[selectedImage]?.url || product.images[0].url}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Package className="w-24 h-24 text-brand-gold/20" />
                  </div>
                )}
              </div>

              {/* Thumbnails */}
              {product.images.length > 1 && (
                <div className="grid grid-cols-4 gap-4">
                  {product.images.map((image, index) => (
                    <div
                      key={image.id}
                      className={`aspect-square bg-brand-ink rounded-lg overflow-hidden cursor-pointer border-2 transition-colors ${
                        selectedImage === index ? 'border-brand-gold' : 'border-transparent hover:border-brand-gold/50'
                      }`}
                      onClick={() => setSelectedImage(index)}
                    >
                      <img
                        src={image.url}
                        alt={`${product.name} - ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>

          {/* Product Info */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
            className="space-y-6"
          >
            <div>
              <h1 className="text-4xl font-serif text-brand-gold mb-2">{product.name}</h1>
              {product.category && (
                <p className="text-brand-ivory/60 text-sm">{product.category}</p>
              )}
            </div>

            <div className="flex items-baseline gap-4">
              <p className="text-4xl font-bold text-brand-gold">{formatPrice(product.price)}</p>
              {product.stock !== undefined && product.stock !== null && (
                <p className={`text-sm ${isOutOfStock ? 'text-red-400' : 'text-brand-ivory/60'}`}>
                  {isOutOfStock ? 'Esgotado' : `${product.stock} em estoque`}
                </p>
              )}
            </div>

            {product.description && (
              <div>
                <h3 className="text-lg font-semibold text-brand-ivory mb-2">Descrição</h3>
                <p className="text-brand-ivory/70 leading-relaxed whitespace-pre-line">
                  {product.description}
                </p>
              </div>
            )}

            <div className="border-t border-brand-gold/20 pt-6">
              {/* Quantity Selector */}
              <div className="flex items-center gap-4 mb-6">
                <span className="text-brand-ivory">Quantidade:</span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={decrementQuantity}
                    disabled={quantity <= 1 || isOutOfStock}
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                  <span className="text-brand-ivory font-semibold w-12 text-center">{quantity}</span>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={incrementQuantity}
                    disabled={isOutOfStock || (product.stock !== null && quantity >= product.stock!)}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Add to Cart Button */}
              <Button
                className="w-full h-14 text-lg"
                onClick={handleAddToCart}
                disabled={isOutOfStock}
              >
                <ShoppingCart className="w-5 h-5 mr-2" />
                {isOutOfStock ? 'Produto Esgotado' : 'Adicionar ao Carrinho'}
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </StoreLayout>
  );
}
